import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import { CART_CONSTANTS } from '../FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import VariantOptionModalMobile from '../VariantOptionModal/VariantOptionModalMobile';
import 'react-toastify/dist/ReactToastify.css';
import './styles/ProductCardsDMobile.css';

interface CategoryCard {
  id: number;
  name: string;
  image: string;
  alt: string;
}

interface ProductCard {
  id: number;
  name: string;
  image: string;
  alt: string;
  slug: string;
  price?: string;
  quantity?: number;
  _id?: string;
  productData?: any;
}

interface ProductCardsDProps {
  categoryCards: CategoryCard[];
  productCards: ProductCard[];
  heading: string;
  onCategoryClick?: (category: string) => void;
  loading?: boolean;
  error?: string | null;
  onAddToCart?: (product: any) => void;
  onWishlistToggle?: (product: any) => void;
  onMoreInfo?: (product: any) => void;
  onViewOptions?: (product: any) => void;
  isItemInWishlist?: (id: string) => boolean;
  products?: any[];
}

const convertVariantsForDisplay = (product: any) => {
  if (!product.variants || product.variants.length === 0) {
    return [{
      id: '',
      size: product.name || product.title,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      rollLength: 1,
      attributes: {},
      variantImage: undefined,
      variantImages: [],
    }];
  }
  return product.variants.map((variant: any) => {
    let variantDisplayName = variant.variantTitle || variant.sku;
    if (variant.attributes) {
      const attributeValues = Object.values(variant.attributes);
      if (attributeValues.length > 0) {
        variantDisplayName = attributeValues[0] as string;
      }
    }
    const baseVariant = {
      id: variant.sku || variant._id,
      size: variantDisplayName,
      price: variant.price || product.price,
      compareAtPrice: variant.compareAtPrice,
      pricePerMeter: (variant.price || product.price) / (variant.weight || 1),
      rollLength: variant.weight || 90,
      attributes: variant.attributes || {},
      variantImage: variant.variantImages?.[0] || variant.variantImage || undefined,
      variantImages: variant.variantImages || [],
      ...variant
    };
    return baseVariant;
  });
};

const detectVariantHierarchy = (variants: any[]): string[] | undefined => {
  if (!variants || variants.length === 0) return undefined;
  
  const firstVariant = variants[0];
  if (!firstVariant.attributes || typeof firstVariant.attributes !== 'object') {
    return undefined;
  }
  
  if (firstVariant.variantLevels) {
    return Object.keys(firstVariant.variantLevels).map((key: string) => {
      const match = key.match(/level(\d+)/i);
      if (match) {
        return firstVariant.variantLevels[key];
      }
      return firstVariant.variantLevels[key];
    });
  }
  
  const attributeKeys = Object.keys(firstVariant.attributes);
  if (attributeKeys.length > 0 && attributeKeys.length <= 3) {
    return attributeKeys;
  }
  
  return undefined;
};

const ProductCardsDMobile: React.FC<ProductCardsDProps> = ({ 
  categoryCards, 
  productCards, 
  heading, 
  onCategoryClick, 
  loading, 
  error,
  onAddToCart,
  onWishlistToggle,
  onMoreInfo,
  isItemInWishlist,
}) => {
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  // Slider states
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(
    categoryCards && categoryCards.length > 0 ? categoryCards[0].name : ''
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [modalProduct, setModalProduct] = useState<any>(null);
  const cardTouchRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  // Memoize modal data to prevent prop changes on every render
  const modalData = useMemo(() => {
    if (!modalProduct) return null;
    return {
      variants: modalProduct.variants ? convertVariantsForDisplay(modalProduct) : [],
      hierarchicalVariants: modalProduct.variants,
      variantHierarchy: modalProduct.variantHierarchy || detectVariantHierarchy(modalProduct.variants || [])
    };
  }, [modalProduct]);

  // 6 products per page (2 rows × 3 columns)
  const PRODUCTS_PER_PAGE = 6;
  const totalPages = Math.ceil(productCards.length / PRODUCTS_PER_PAGE);
  
  useEffect(() => {
    setCurrentPage(0);
  }, [productCards]);

  // Enhanced navigation functions with smooth scrolling
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      scrollToPage(newPage);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      scrollToPage(newPage);
    }
  };

  const scrollToPage = (page: number) => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({
        left: page * container.offsetWidth,
        behavior: 'smooth'
      });
    }
  };

  // Handle touch and mouse events for swiping
  const handleTouchStart = (e: React.TouchEvent) => {
    const container = scrollContainerRef.current;
    if (container) {
      setIsDragging(true);
      setStartX(e.touches[0].pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
      container.style.scrollBehavior = 'auto';
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const x = e.touches[0].pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5; // Adjust scroll speed
    container.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.style.scrollBehavior = 'smooth';
      // Snap to nearest page
      const newPage = Math.round(container.scrollLeft / container.offsetWidth);
      setCurrentPage(Math.min(Math.max(0, newPage), totalPages - 1));
      scrollToPage(Math.min(Math.max(0, newPage), totalPages - 1));
    }
    setIsDragging(false);
  };

  // Mouse event handlers for desktop touchpads
  const handleMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (container) {
      setIsDragging(true);
      setStartX(e.pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
      container.style.scrollBehavior = 'auto';
      container.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    
    const container = scrollContainerRef.current;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5; // Match touch scroll speed
    container.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.style.scrollBehavior = 'smooth';
      container.style.cursor = 'grab';
      // Snap to nearest page
      const newPage = Math.round(container.scrollLeft / container.offsetWidth);
      setCurrentPage(Math.min(Math.max(0, newPage), totalPages - 1));
      scrollToPage(Math.min(Math.max(0, newPage), totalPages - 1));
    }
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      const container = scrollContainerRef.current;
      if (container) {
        container.style.scrollBehavior = 'smooth';
        container.style.cursor = 'grab';
        // Snap to nearest page when leaving while dragging
        const newPage = Math.round(container.scrollLeft / container.offsetWidth);
        setCurrentPage(Math.min(Math.max(0, newPage), totalPages - 1));
        scrollToPage(Math.min(Math.max(0, newPage), totalPages - 1));
      }
      setIsDragging(false);
    }
  };

  const handleAddToCartClick = (productCard: ProductCard, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();

    if (onAddToCart && productCard.productData) {
      onAddToCart(productCard.productData);
    }
  };

  const handleCartQuantityChange = (cartItem: any, newQuantity: number) => {
    if (!cartItem) return;

    if (newQuantity <= 0) {
      removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
    } else {
      updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
    }
  };

  const handleWishlistClick = (productCard: ProductCard, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();

    if (onWishlistToggle && productCard.productData) {
      onWishlistToggle(productCard.productData);
    }
  };

  const handleCardClick = (productCard: ProductCard) => {
    if (isDragging) return;
    if (onMoreInfo && productCard.productData) {
      onMoreInfo(productCard.productData);
    }
  };

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setCurrentPage(0);
    if (onCategoryClick) {
      onCategoryClick(categoryName);
    }
  };

  // Pagination dots click handler
  const handleDotClick = (pageIndex: number) => {
    setCurrentPage(pageIndex);
    const container = scrollContainerRef.current;
    if (container) {
      const pageWidth = container.offsetWidth;
      container.scrollTo({
        left: pageIndex * pageWidth,
        behavior: 'smooth'
      });
    }
  };

  // Create pages array for rendering
  const pages = [];
  for (let i = 0; i < totalPages; i++) {
    const startIndex = i * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;
    pages.push(productCards.slice(startIndex, endIndex));
  }

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevPage();
      } else if (e.key === 'ArrowRight') {
        goToNextPage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPage, totalPages]);

  return (
    <div className="product-cards-d-mobile">
      <div className="product-cards-d-mobile__left">
        <div className="product-cards-d-mobile__categories">
          {categoryCards.map((card) => (
            <div 
              key={card.id} 
              className={`product-cards-d-mobile__category-card ${selectedCategory === card.name ? 'product-cards-d-mobile__category-card--selected' : ''}`}
              onClick={() => handleCategoryClick(card.name)} 
              style={{ cursor: 'pointer' }}
            >
              <div className="product-cards-d-mobile__category-image-container">
                <img 
                  src={card.image} 
                  alt={card.alt}
                  className="product-cards-d-mobile__category-image"
                  draggable="false"
                />
              </div>
              <h3 className="product-cards-d-mobile__category-name">{card.name}</h3>
            </div>
          ))}
        </div>
      </div>
      
      <div className="product-cards-d-mobile__right">
        <div className="product-cards-d-mobile__header">
          <h2 className="product-cards-d-mobile__heading">{heading}</h2>
          <div className="product-cards-d-mobile__nav-buttons">
            <button 
              className={`product-cards-d-mobile__nav-button ${currentPage === 0 ? 'disabled' : ''}`}
              onClick={goToPrevPage}
              disabled={currentPage === 0}
              aria-label="Previous page"
            >
              <ChevronLeft size={20} />
            </button>
            <button 
              className={`product-cards-d-mobile__nav-button ${currentPage >= totalPages - 1 ? 'disabled' : ''}`}
              onClick={goToNextPage}
              disabled={currentPage >= totalPages - 1}
              aria-label="Next page"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
        
        {loading && (
          <div className="loading-spinner">Loading products...</div>
        )}
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {!loading && !error && productCards.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>No products found for this category.</p>
          </div>
        )}
        
        {!loading && productCards.length > 0 && (
          <div className="product-cards-d-mobile__slider-wrapper">
            <div 
              className={`product-cards-d-mobile__products-container ${isDragging ? 'dragging' : ''}`}
              ref={scrollContainerRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              style={{
                display: 'flex',
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                scrollBehavior: 'smooth',
                WebkitOverflowScrolling: 'touch',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                width: '100%',
                position: 'relative',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                touchAction: 'pan-y',
              }}
            >
              {pages.map((pageProducts, pageIndex) => (
                <div 
                  key={pageIndex} 
                  className="product-cards-d-mobile__page"
                >
                  <div className="product-cards-d-mobile__products">
                    {pageProducts.map((card) => {
                      const isWishlisted = isItemInWishlist && card._id ? isItemInWishlist(card._id) : false;
                      const cartItem = cartItems && cartItems.find((item: any) => {
                        if (item.productId !== (card._id || String(card.id))) return false;
                        return !item.variantSku || item.variantSku === '';
                      });
                      const isInCart = !!cartItem;
                      const cartQuantity = cartItem?.quantity || 0;
                      // Assume card.quantity is available, fallback to 1 if not
                      const quantity = typeof card.quantity === 'number' ? card.quantity : 1;
                      return (
                        <div 
                          key={card.id} 
                          className="product-cards-d-mobile__product-card"
                          onClick={() => handleCardClick(card)}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            cardTouchRef.current = {
                              x: e.touches[0].clientX,
                              y: e.touches[0].clientY,
                              moved: false
                            };
                          }}
                          onTouchMove={(e) => {
                            const touch = cardTouchRef.current;
                            if (!touch) return;
                            const deltaX = Math.abs(e.touches[0].clientX - touch.x);
                            const deltaY = Math.abs(e.touches[0].clientY - touch.y);
                            if (deltaX > 6 || deltaY > 6) {
                              cardTouchRef.current = { ...touch, moved: true };
                            }
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            const touch = cardTouchRef.current;
                            cardTouchRef.current = null;
                            if (touch?.moved) return;
                            if (!isDragging) {
                              handleCardClick(card);
                            }
                          }}
                          style={{ 
                            cursor: 'pointer',
                            position: 'relative',
                            touchAction: 'pan-y'
                          }}
                        >
                          {/* Out of Stock Badge */}
                          {quantity === 0 && (
                            <div style={{position: 'absolute', top: 8, left: 8, background: '#ccc', color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '0.8rem', zIndex: 20}}>
                              Out of Stock
                            </div>
                          )}
                          {/* Wishlist Icon */}
                          <div 
                            className="product-cards-d-mobile__wishlist-icon" 
                            title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleWishlistClick(card, e);
                            }}
                            onTouchStart={(e) => e.stopPropagation()}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              handleWishlistClick(card, e);
                            }}
                            role="button"
                            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                            style={{
                              position: 'absolute',
                              zIndex: 2,
                              pointerEvents: 'auto'
                            }}
                          >
                            <Heart 
                              size={16} 
                              color={isWishlisted ? '#e0245e' : '#000000ff'} 
                              strokeWidth={2.2} 
                              fill={isWishlisted ? '#e0245e' : 'none'} 
                            />
                          </div>

                          <div className="product-cards-d-mobile__product-image-container">
                            <img 
                              src={card.image} 
                              alt={card.alt}
                              className="product-cards-d-mobile__product-image"
                              draggable="false"
                            />
                          </div>
                          
                          <div className="product-cards-d-mobile__product-content">
                            <h4 className="product-cards-d-mobile__product-name">{card.name}</h4>
                            <div className="product-cards-d-mobile__price-button-container">
                              {!card.productData?.variants || card.productData.variants.length === 0 ? (
                                <p className="product-cards-d-mobile__product-price">
                                  {(() => {
                                    const match = typeof card.price === 'string' ? card.price.match(/([\d,.]+)/) : null;
                                    if (match) {
                                      const num = parseFloat(match[1].replace(/,/g, ''));
                                      if (!isNaN(num)) {
                                        return card.price!.replace(match[1], Math.round(num).toString());
                                      }
                                    }
                                    return card.price ?? '';
                                  })()}
                                </p>
                              ) : null}
                              {isInCart ? (
                                <div className="cart-quantity-controls">
                                  <button
                                    type="button"
                                    className="cart-quantity-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCartQuantityChange(cartItem, cartQuantity - 1);
                                    }}
                                    onTouchEnd={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleCartQuantityChange(cartItem, cartQuantity - 1);
                                    }}
                                    style={{ 
                                      cursor: 'pointer', 
                                      position: 'relative', 
                                      zIndex: 10,
                                      WebkitTapHighlightColor: 'transparent',
                                      pointerEvents: 'auto'
                                    }}
                                    aria-label="Decrease quantity"
                                  >
                                    -
                                  </button>
                                  <span className="cart-quantity-display">{cartQuantity}</span>
                                  <button
                                    type="button"
                                    className="cart-quantity-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
                                        handleCartQuantityChange(cartItem, cartQuantity + 1);
                                      }
                                    }}
                                    onTouchEnd={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
                                        handleCartQuantityChange(cartItem, cartQuantity + 1);
                                      }
                                    }}
                                    style={{ 
                                      cursor: 'pointer', 
                                      position: 'relative', 
                                      zIndex: 10,
                                      WebkitTapHighlightColor: 'transparent',
                                      pointerEvents: 'auto'
                                    }}
                                    aria-label="Increase quantity"
                                    disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
                                  >
                                    +
                                  </button>
                                </div>
                              ) : card.productData?.variants && card.productData.variants.length > 0 ? (
                                <button 
                                  type="button"
                                  className="product-cards-d-mobile__view-options"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setModalProduct(card.productData);
                                  }}
                                  onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setModalProduct(card.productData);
                                  }}
                                  style={quantity === 0 ? { backgroundColor: '#ccc', color: '#999', cursor: 'not-allowed' } : {}}
                                  disabled={quantity === 0}
                                >
                                  View Options
                                </button>
                              ) : (
                                <button 
                                  type="button"
                                  className="product-cards-d-mobile__add-cart"
                                  onClick={(e) => handleAddToCartClick(card, e)}
                                  onTouchEnd={(e) => handleAddToCartClick(card, e as any)}
                                  style={quantity === 0 ? { backgroundColor: '#ccc', color: '#888', cursor: 'not-allowed' } : {}}
                                  disabled={quantity === 0}
                                >
                                  Add
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination Dots */}
            {totalPages > 1 && (
              <div className="product-cards-d-mobile__pagination">
                {pages.map((_, index) => (
                  <button
                    key={index}
                    className={`product-cards-d-mobile__dot ${currentPage === index ? 'active' : ''}`}
                    onClick={() => handleDotClick(index)}
                    aria-label={`Go to page ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Variant Options Modal */}
      {modalProduct && modalData && (
        <VariantOptionModalMobile
          isOpen={!!modalProduct}
          onClose={() => setModalProduct(null)}
          product={modalProduct}
          variants={modalData.variants}
          hierarchicalVariants={modalData.hierarchicalVariants}
          variantHierarchy={modalData.variantHierarchy}
        />
      )}
    </div>
  );
};

export default ProductCardsDMobile;